import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sun, Github, Cloud, PenTool, ShieldCheck, Zap, Heart,
  ArrowRight, Check, Send, Church, Sparkles,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1516117172878-fd2c41f4a759?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzbW9reSUyMG1vdW50YWlucyUyMHN1bnJpc2UlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzOTA0NjQ2fDA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG =
  "https://images.unsplash.com/photo-1622598453695-4fbaf151aadc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxjaHVyY2glMjB3b3JzaGlwJTIwY29tbXVuaXR5JTIwZ2F0aGVyaW5nfGVufDB8fHx8MTc4MzkwNDY0Nnww&ixlib=rb-4.1.0&q=85";
const DASH_IMG =
  "https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB1c2luZyUyMGxhcHRvcCUyMGJyaWdodCUyMG1vZGVybiUyMGRlc2t8ZW58MHx8fHwxNzgzOTA0NjQ3fDA&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const services = [
  { icon: PenTool, title: "Purposeful Design", desc: "Beautiful, mobile-ready websites crafted to reflect your ministry's heart and mission." },
  { icon: Github, title: "GitHub Hosting", desc: "Every client site lives in version-controlled GitHub repositories — reliable, transparent, and always recoverable." },
  { icon: Cloud, title: "Azure Infrastructure", desc: "Fast, secure static hosting on Microsoft Azure so your congregation reaches you without a hitch." },
  { icon: ShieldCheck, title: "Secure & Maintained", desc: "SSL, backups, and ongoing care included. Focus on ministry; we'll handle the technology." },
];

const steps = [
  { n: "01", title: "Share Your Vision", desc: "Tell us about your church or ministry, your congregation, and the message you want to share online." },
  { n: "02", title: "We Build & Host", desc: "We design your static site, commit it to GitHub, and deploy it on Azure — fast, secure, and free of maintenance headaches." },
  { n: "03", title: "Reach the Masses", desc: "Your site goes live. Manage content through your client dashboard and shine His light across Tennessee and beyond." },
];

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", ministry_name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/leads", form);
      toast.success(data.message || "Thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", ministry_name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="bg-[#FAFAFA] overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Sunrise over the Smoky Mountains" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <motion.div initial="hidden" animate="show" variants={fade} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-600/20 border border-amber-500/40 text-amber-300 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase">
              <Sparkles className="h-3.5 w-3.5" /> Faith-Based Web Design · Tennessee
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              Let your ministry be the <span className="text-amber-400">light</span> — even in technology.
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-xl">
              We build and host stunning websites for churches and ministries on GitHub and Azure —
              so you can reach the masses with the truth that Jesus is the way, the truth, and the life.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-btn"
                className="inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 hover:scale-105 text-white font-semibold px-7 py-3.5 transition-all duration-200 shadow-xl shadow-amber-600/30"
              >
                Start Your Website <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#how"
                data-testid="hero-learn-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-7 py-3.5 backdrop-blur-md transition-colors duration-200"
              >
                How It Works
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 max-w-7xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="max-w-2xl">
          <p className="text-amber-700 font-semibold text-sm uppercase tracking-widest">What We Do</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
            Technology in service of the Gospel
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Everything your ministry needs to establish a trustworthy, beautiful presence online — handled with care.
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              transition={{ delay: i * 0.08 }}
              data-testid={`service-card-${i}`}
              className="group rounded-2xl bg-white border border-slate-200 p-7 shadow-[0_8px_32px_rgba(0,0,0,0.05)] hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgba(217,119,6,0.12)] transition-all duration-300"
            >
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-amber-50 text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors duration-300">
                <s.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 bg-slate-900 text-white relative grain">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-400 font-semibold text-sm uppercase tracking-widest">The Journey</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">
                From vision to a living website in three steps
              </h2>
              <div className="mt-10 space-y-8">
                {steps.map((st) => (
                  <div key={st.n} className="flex gap-5" data-testid={`step-${st.n}`}>
                    <span className="font-display text-3xl font-bold text-amber-500 shrink-0">{st.n}</span>
                    <div>
                      <h3 className="font-display text-xl font-semibold">{st.title}</h3>
                      <p className="mt-1 text-slate-400 leading-relaxed">{st.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={DASH_IMG} alt="Managing a church website" className="rounded-3xl shadow-2xl" />
              <div className="absolute -bottom-6 -left-6 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20 p-5 max-w-[240px]">
                <div className="flex items-center gap-2 text-amber-400">
                  <Zap className="h-5 w-5" />
                  <span className="font-semibold">Deployed on Azure</span>
                </div>
                <p className="mt-1 text-sm text-slate-200">Lightning-fast static sites, version-controlled on GitHub.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR WORK / MISSION */}
      <section id="work" className="py-24 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <img src={COMMUNITY_IMG} alt="Worship community gathering" className="rounded-3xl shadow-xl" />
            <div className="absolute -top-6 -right-6 rounded-2xl bg-amber-600 text-white p-5 shadow-xl shadow-amber-600/30">
              <Church className="h-7 w-7" />
              <p className="mt-2 font-display text-2xl font-bold">Built for His Body</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-amber-700 font-semibold text-sm uppercase tracking-widest">Our Heart</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              More than websites — a mission field
            </h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">
              We believe the internet is one of the greatest mission fields of our time. Every church we serve
              becomes a lighthouse online — welcoming the searching, encouraging the faithful, and pointing
              all people to Christ.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Sermon & event pages that keep your congregation connected",
                "Online giving and contact ready out of the box",
                "Fast, secure, and built to grow with your ministry",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 flex items-center gap-2 font-display text-lg italic text-slate-800">
              <Heart className="h-5 w-5 text-amber-600" />
              {'"Let your light shine before others." — Matthew 5:16'}
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 bg-[#F3F4F6]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl bg-white border border-slate-200 shadow-[0_16px_48px_rgba(0,0,0,0.08)] overflow-hidden grid md:grid-cols-5">
            <div className="md:col-span-2 bg-slate-900 text-white p-10 relative grain">
              <h2 className="font-display text-3xl font-bold tracking-tight">{"Let's build together"}</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                {"Tell us about your ministry and we'll reach out to start bringing your vision to life."}
              </p>
              <div className="mt-10 space-y-3 text-sm text-slate-300">
                <p className="flex items-center gap-2"><Sun className="h-4 w-4 text-amber-500" /> jason.ferguson@tnwebdesign.org</p>
                <p className="flex items-center gap-2"><Cloud className="h-4 w-4 text-amber-500" /> Serving Tennessee & Beyond</p>
              </div>
            </div>
            <form onSubmit={submit} data-testid="lead-form" className="md:col-span-3 p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Your Name *" testid="lead-name">
                  <input required value={form.name} onChange={set("name")} data-testid="lead-name-input" className="input" placeholder="Pastor John" />
                </Field>
                <Field label="Email *" testid="lead-email">
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="lead-email-input" className="input" placeholder="you@church.org" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Ministry / Church" testid="lead-ministry">
                  <input value={form.ministry_name} onChange={set("ministry_name")} data-testid="lead-ministry-input" className="input" placeholder="Grace Community Church" />
                </Field>
                <Field label="Phone" testid="lead-phone">
                  <input value={form.phone} onChange={set("phone")} data-testid="lead-phone-input" className="input" placeholder="(615) 555-0100" />
                </Field>
              </div>
              <Field label="How can we serve you? *" testid="lead-message">
                <textarea required rows={4} value={form.message} onChange={set("message")} data-testid="lead-message-input" className="input resize-none" placeholder="Tell us about your ministry and what you're hoping for..." />
              </Field>
              <button
                type="submit"
                disabled={sending}
                data-testid="lead-submit-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold px-7 py-3.5 w-full sm:w-auto transition-colors duration-200"
              >
                {sending ? "Sending..." : "Send Message"} <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .input {
          width: 100%;
          border: 1px solid hsl(214 20% 90%);
          border-radius: 0.6rem;
          padding: 0.7rem 0.9rem;
          font-size: 0.9rem;
          background: #fff;
          box-shadow: inset 0 1px 2px rgba(0,0,0,0.03);
          transition: border-color .2s, box-shadow .2s;
        }
        .input:focus { outline: none; border-color: hsl(32 95% 44%); box-shadow: 0 0 0 3px hsl(32 95% 44% / 0.15); }
      `}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={`field-${testid}`}>
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
